import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  Input,
  Text,
} from "@chakra-ui/react";
import { Field, FieldInputProps, FormikProps, useFormikContext } from "formik";
import AppButton from "@/features/line-sheet-sets/app-button";
import BusinessRegistrationCertificateRow, {
  BusinessRegistrationCertificateStatus,
} from "@/shared/components/business-registration-certificate-row";
import StatusBadge from "@/shared/components/StatusBadge";
import useGetBuyerBusinessLicense from "@/shared/hooks/business-license/useGetBuyerBusinessLicense";
import useParseBusinessRegistrationCertificate from "@/shared/hooks/business-registration-certificate/useParseBusinessRegistrationCertificate";
import {
  BUSINESS_REGISTRATION_CERTIFICATE_ACCEPT,
  formatBusinessRegistrationNumber,
  isAcceptableBusinessRegistrationCertificateType,
  MAX_BUSINESS_REGISTRATION_CERTIFICATE_FILE_SIZE,
} from "@/shared/constants/business-registration-certificate";

// 이 섹션이 읽고 쓰는 폼 값 — 감싸는 폼(PlanChangeFormValues)이 이 인터페이스를 확장한다.
export interface MembershipBusinessInfoFormValues {
  // 새로 인식한 uno_file id 또는 이미 등록된 사업자등록증 id. null이면 신청할 수 없다.
  certificateFileId: number | null;
  businessRegistrationNumber: string;
  businessName: string;
  taxEmail: string;
}

interface Props {
  // 자동 인식 중에는 신청 버튼을 잠가야 해서 진행 여부만 위로 알린다.
  onParsingChange?: (isParsing: boolean) => void;
}

// 회원정보 수정의 "사업자 정보" 카드(BusinessInfoCard)와 같은 구성으로,
// 모달 안에서 사업자등록증을 등록·변경하고 자동 인식 결과를 확인·수정하게 한다.
// 저장은 이 섹션이 하지 않는다 — 감싸는 모달이 신청 직전에 한 번에 저장한다.
function PathPickMembershipBusinessInfoSection({ onParsingChange }: Props) {
  const { values, setValues } =
    useFormikContext<MembershipBusinessInfoFormValues>();
  const { businessLicense, isPending: isBusinessLicenseLoading } =
    useGetBuyerBusinessLicense();
  const { parseBusinessRegistrationCertificate, isPending: isParsing } =
    useParseBusinessRegistrationCertificate();

  const fileInputRef = useRef<HTMLInputElement>(null);
  // 새 탭에 띄운 파일 URL — 탭이 살아 있어야 해서 언마운트할 때 정리한다.
  const objectUrlsRef = useRef<string[]>([]);

  // 자동 인식까지 끝났지만 아직 저장(신청)하지 않은 파일.
  // 저장 전에는 서버 URL이 없어 파일 보기에 원본 File이 필요하다.
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  // 인식 중에는 아직 등록이 확정되지 않아 파일명만 먼저 보여준다.
  const [parsingFileName, setParsingFileName] = useState<string | null>(null);
  // 파일 관련 안내는 Formik 에러와 별개로 관리한다.
  const [fileError, setFileError] = useState<string | null>(null);
  // 이번 화면에서 자동 인식을 거쳤는지 — 조회로 복원한 경우에는 인식 배지를 띄우지 않는다.
  const [isAutoRecognized, setIsAutoRecognized] = useState(false);
  const [hasMissingFields, setHasMissingFields] = useState(false);

  useEffect(() => {
    // 배열 자체는 교체되지 않고 push만 하므로 여기서 붙잡아 둔 참조로 정리해도 안전하다.
    const objectUrls = objectUrlsRef.current;
    return () => {
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  useEffect(() => {
    onParsingChange?.(isParsing);
  }, [isParsing, onParsingChange]);

  const status: BusinessRegistrationCertificateStatus = isParsing
    ? "PARSING"
    : pendingFile
    ? "UNSAVED"
    : businessLicense
    ? "SAVED"
    : isBusinessLicenseLoading
    ? "LOADING"
    : "EMPTY";

  const registeredAt = useMemo(
    () =>
      businessLicense?.registeredAt
        ? new Date(businessLicense.registeredAt)
        : null,
    [businessLicense]
  );

  const openFilePicker = () => {
    if (isParsing) {
      return;
    }
    fileInputRef.current?.click();
  };

  const handleViewFile = () => {
    // 저장 전 파일은 아직 서버에 연결되지 않아 로컬 objectURL로 띄운다.
    if (pendingFile) {
      const url = URL.createObjectURL(pendingFile);
      objectUrlsRef.current.push(url);
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    if (businessLicense) {
      // 응답 url은 inline presigned URL이라 새 탭에서 바로 열린다.
      window.open(businessLicense.url, "_blank", "noopener,noreferrer");
    }
  };

  // 파일을 고르면 파싱 API를 호출해 사업자등록번호·사업자명을 채운다.
  const handleFileSelect = async (file: File) => {
    setFileError(null);
    setHasMissingFields(false);

    if (!isAcceptableBusinessRegistrationCertificateType(file)) {
      setFileError("PDF, JPG, JPEG, PNG 파일만 첨부할 수 있습니다.");
      return;
    }
    if (file.size > MAX_BUSINESS_REGISTRATION_CERTIFICATE_FILE_SIZE) {
      setFileError("파일 크기는 최대 10MB까지 첨부할 수 있습니다.");
      return;
    }

    // 새 파일을 고르면 이전 파싱 결과는 무효다.
    setPendingFile(null);
    setIsAutoRecognized(false);
    setParsingFileName(file.name);
    try {
      const { unoFileId, result } = await parseBusinessRegistrationCertificate(
        file
      );
      setHasMissingFields(result.missingRequiredFields.length > 0);
      setIsAutoRecognized(true);
      setPendingFile(file);
      setValues((prev) => ({
        ...prev,
        certificateFileId: unoFileId,
        // 응답 필드는 모두 nullable이라 못 읽은 항목은 빈 값으로 두고 사용자가 채운다.
        businessRegistrationNumber: result.businessRegistrationNumber ?? "",
        businessName: result.companyName ?? "",
        // 이미 입력한 이메일이 있으면 사용자가 고른 값을 우선한다.
        taxEmail: prev.taxEmail || (result.eTaxInvoiceEmail ?? ""),
      }));
    } catch {
      // 파싱에 실패하면 이전 등록 상태를 그대로 두고 안내만 노출한다.
      setFileError("사업자등록증을 읽지 못했습니다. 다른 파일을 첨부해 주세요.");
    } finally {
      setParsingFileName(null);
    }
  };

  const isRegistered = values.certificateFileId !== null;

  return (
    <Flex flexDirection={"column"} gap={"12px"}>
      <input
        type={"file"}
        style={{ display: "none" }}
        ref={fileInputRef}
        accept={BUSINESS_REGISTRATION_CERTIFICATE_ACCEPT}
        onChange={(event) => {
          const file = event.target.files?.[0];
          // 같은 파일을 다시 선택해도 change 이벤트가 발생하도록 초기화한다.
          event.target.value = "";
          if (file) {
            handleFileSelect(file);
          }
        }}
      />
      <Flex flexDirection={"column"} gap={"8px"}>
        <BusinessRegistrationCertificateRow
          status={status}
          fileName={
            parsingFileName ??
            pendingFile?.name ??
            businessLicense?.name ??
            null
          }
          registeredAt={registeredAt}
          onViewFile={handleViewFile}
        />
        <Text fontSize={"12px"} color={fileError ? "#CC1D1D" : "#8F8F8C"}>
          {fileError ?? "PDF, JPG, JPEG, PNG · 최대 10MB"}
        </Text>
      </Flex>

      <Flex
        flexDirection={"column"}
        gap={"8px"}
        padding={"16px"}
        borderRadius={"4px"}
        border={"0.5px solid #EFEFED"}
        background={"#F7F8FA"}
      >
        <Flex
          flexDirection={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          gap={"8px"}
          flexWrap={"wrap"}
          rowGap={"8px"}
        >
          <AppButton
            variant={"primary"}
            size={"small"}
            isDisabled={isParsing}
            onClick={openFilePicker}
          >
            {isRegistered ? "사업자등록증 변경" : "사업자등록증 등록"}
          </AppButton>
          {isAutoRecognized ? (
            hasMissingFields ? (
              <StatusBadge
                status={"일부 항목 확인 필요"}
                colorVariant={"yellow"}
              />
            ) : (
              <StatusBadge status={"자동 인식 완료"} colorVariant={"green"} />
            )
          ) : (
            // 조회로 복원만 된 상태에서는 이번 화면에서 인식한 게 없어 배지를 띄우지 않는다.
            !isRegistered && (
              <StatusBadge status={"등록 후 자동 입력"} colorVariant={"gray"} />
            )
          )}
        </Flex>
        <Flex flexDirection={"column"} gap={"2px"}>
          <Text fontSize={"12px"} color={"#6F6F6F"}>
            사업자등록증을 등록하면 사업자등록번호와 사업자명을 자동으로
            불러옵니다.
          </Text>
          {isAutoRecognized &&
            (hasMissingFields ? (
              <Text fontSize={"12px"} color={"#DE7000"}>
                일부 항목을 자동으로 채우지 못했습니다. 값을 확인해 주세요.
              </Text>
            ) : (
              <Text fontSize={"12px"} color={"#1272EF"}>
                자동으로 입력된 정보를 확인하고 필요한 경우 수정해 주세요.
              </Text>
            ))}
        </Flex>

        <Grid
          gridTemplateColumns={{ base: "1fr", md: "1fr 1fr" }}
          columnGap={"16px"}
          rowGap={"12px"}
          paddingTop={"8px"}
        >
          <Field name={"businessRegistrationNumber"}>
            {({
              field,
              form,
            }: {
              field: FieldInputProps<string>;
              form: FormikProps<MembershipBusinessInfoFormValues>;
            }) => (
              <FormControl
                isRequired
                isInvalid={
                  !!form.errors.businessRegistrationNumber &&
                  !!form.touched.businessRegistrationNumber
                }
              >
                <FormLabel display={"flex"} m={"0"}>
                  <Text color={"#6F6F6F"} fontSize={"12px"} fontWeight={"400"}>
                    사업자등록번호
                  </Text>
                </FormLabel>
                <Input
                  {...field}
                  fontSize={"12px"}
                  minHeight={"0"}
                  height={"28px"}
                  // 사업자등록증을 등록해야 자동으로 채워지는 칸이라 그전에는 입력을 막는다.
                  isDisabled={!isRegistered}
                  background={isRegistered ? "#FFF" : "#EFEFED"}
                  inputMode={"numeric"}
                  maxLength={12}
                  placeholder={isRegistered ? "123-45-67890" : undefined}
                  onChange={(event) => {
                    form.setFieldValue(
                      field.name,
                      formatBusinessRegistrationNumber(event.target.value)
                    );
                  }}
                />
                {/* 좁은 폭에서 에러 문구가 두 줄로 감기면 고정 높이에 잘린다 */}
                <Box minHeight={"16px"}>
                  <FormErrorMessage m={"0"} mt={"4px"} fontSize={"12px"}>
                    {form.errors.businessRegistrationNumber}
                  </FormErrorMessage>
                </Box>
              </FormControl>
            )}
          </Field>
          <Field name={"businessName"}>
            {({
              field,
              form,
            }: {
              field: FieldInputProps<string>;
              form: FormikProps<MembershipBusinessInfoFormValues>;
            }) => (
              <FormControl
                isRequired
                isInvalid={
                  !!form.errors.businessName && !!form.touched.businessName
                }
              >
                <FormLabel display={"flex"} m={"0"}>
                  <Text color={"#6F6F6F"} fontSize={"12px"} fontWeight={"400"}>
                    사업자명
                  </Text>
                </FormLabel>
                <Input
                  {...field}
                  fontSize={"12px"}
                  minHeight={"0"}
                  height={"28px"}
                  isDisabled={!isRegistered}
                  background={isRegistered ? "#FFF" : "#EFEFED"}
                  placeholder={
                    isRegistered ? "사업자명을 입력해 주세요" : undefined
                  }
                />
                {/* 좁은 폭에서 에러 문구가 두 줄로 감기면 고정 높이에 잘린다 */}
                <Box minHeight={"16px"}>
                  <FormErrorMessage m={"0"} mt={"4px"} fontSize={"12px"}>
                    {form.errors.businessName}
                  </FormErrorMessage>
                </Box>
              </FormControl>
            )}
          </Field>
        </Grid>
      </Flex>
    </Flex>
  );
}

export default PathPickMembershipBusinessInfoSection;
